import type { QueuedAction, SyncQueueStorage } from './types';

/** Legacy photo/submission records carry their owner; old starts do not. Never guess. */
export function queueOwner(action: QueuedAction): string | undefined {
  return (
    action.ownerUid ??
    (typeof action.payload.workerId === 'string'
      ? action.payload.workerId
      : typeof action.payload.uploadedBy === 'string'
        ? action.payload.uploadedBy
        : undefined)
  );
}

/** Bind an operation to the account at its start, even if login changes while awaiting IO. */
export function ownedQueueStorage(
  base: SyncQueueStorage,
  uid: string | undefined,
): SyncQueueStorage {
  const own = async () =>
    (await base.list()).filter((action) => !!uid && queueOwner(action) === uid);
  return {
    list: own,
    async put(action) {
      if (!uid || (queueOwner(action) && queueOwner(action) !== uid))
        throw new Error('Queue owner mismatch');
      await base.put({ ...action, ownerUid: uid });
    },
    async remove(id) {
      if ((await own()).some((action) => action.id === id)) await base.remove(id);
    },
    async clear() {
      for (const action of await own()) await base.remove(action.id);
    },
  };
}

/** Legacy terminal actions refer to an existing sheet; the server still authorizes its owner. */
export async function recoverLegacyActions(
  base: SyncQueueStorage,
  uid: string,
  owns: (action: QueuedAction) => Promise<boolean>,
): Promise<void> {
  for (const action of await base.list()) {
    if (queueOwner(action) || action.completedAt || action.awaitsRef || !action.payload.timesheetId)
      continue;
    if (!['completeWorkDay', 'uploadTimesheetPhoto'].includes(action.type)) continue;
    if (!(await owns(action))) continue;
    await base.put({
      ...action,
      ownerUid: uid,
      payload: {
        ...action.payload,
        expectedWorkerId: uid,
        ...(action.type === 'uploadTimesheetPhoto' ? { uploadedBy: uid } : {}),
      },
    });
  }
}

/** Keep text-only submissions, but never silently send a purchase with a discarded receipt. */
export function withoutMedia(action: QueuedAction): QueuedAction | null {
  if (!action.blob && action.type !== 'uploadTimesheetPhoto') return action;
  const rest = { ...action };
  delete rest.blob;
  delete rest.blobName;
  delete rest.blobType;
  return {
    ...rest,
    failedPermanently: true,
    errorCode: 'MEDIA_REMOVED',
    lastError: 'MEDIA_REMOVED',
  };
}
